/**
 * Drop one folder per person — "Mario Rossi" holding "carta identità.pdf", "codice
 * fiscale.pdf", "contratto.pdf" — and each file is filed under that person's item.
 *
 * Nothing is uploaded on the drop. The dialog shows who each folder was matched to
 * (changeable: a wrong guess is one click), which item each file goes under
 * (changeable, or "do not upload"), and the expiry dates the dated ones need, read
 * from the file name when it has one. Then one button uploads everything, person by
 * person, and a file that fails stays in the list with the reason, to try again.
 *
 * The guessing is in folderImport.ts.
 */

import { useMemo, useRef, useState } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';

import { Combobox } from '@/components/Combobox';
import { Modal } from '@/components/Modal';
import { personOptions } from '@/features/access/people';
import { useDir, useI18n } from '@/i18n';
import { api, ApiError, isAcceptedUpload, isImageUpload, MAX_FILE_BYTES, MAX_IMAGE_BYTES, type DocumentType } from '@/lib/api';
import { dateShort, fileSize } from '@/lib/format';
import { useLabel } from '@/lib/useLabel';

import {
  dateInName,
  groupByFolder,
  isImageFile,
  isPhoto,
  matchPerson,
  matchType,
  readDrop,
  sideOrder,
  type DroppedFolder,
} from './folderImport';

/** Not a document: the person's photo, for their profile and identification card. */
const PHOTO = '__photo';

interface Row {
  key: string;
  file: File;
  /** The item it goes under; PHOTO for the person's photo; '' leaves it out. */
  typeCode: string;
  expiresAt: string;
  state: 'ready' | 'done' | 'failed';
  /** Why it cannot go at all: a file type the portal refuses, a file too big. */
  problem?: string;
  /** Why the last try failed; it is tried again with the next upload. */
  error?: string;
}

interface Group {
  key: string;
  folder: string;
  personId: string;
  /** Matched from the folder's name, rather than picked by hand. */
  guessed: boolean;
  rows: Row[];
}

let seq = 0;
const nextKey = () => `k${++seq}`;

export function FolderUploadDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useI18n();
  const { forward } = useDir();
  const L = useLabel();
  const queryClient = useQueryClient();
  const picker = useRef<HTMLInputElement>(null);
  const filePicker = useRef<HTMLInputElement>(null);
  const [groups, setGroups] = useState<Group[]>([]);
  const [over, setOver] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);

  const users = useQuery({ queryKey: ['users'], queryFn: () => api.users(), enabled: open });
  const types = useQuery({ queryKey: ['document-types'], queryFn: () => api.documentTypes(), enabled: open });

  const people = useMemo(() => (users.data?.users ?? []).filter((u) => u.status !== 'archived'), [users.data]);
  const workerTypes = useMemo(() => (types.data?.types ?? []).filter((x) => x.scope === 'worker'), [types.data]);
  const typeOf = (code: string): DocumentType | undefined => workerTypes.find((x) => x.code === code);

  // What each chosen person already has on file, to say what an upload replaces.
  const chosen = [...new Set(groups.map((g) => g.personId).filter(Boolean))];
  const registers = useQueries({
    queries: chosen.map((id) => ({ queryKey: ['worker-register', id], queryFn: () => api.workerRegister(id), enabled: open })),
  });
  const onFile = (personId: string, code: string) => {
    const register = registers[chosen.indexOf(personId)]?.data;
    return register?.items.find((i) => i.code === code && i.documentId);
  };

  /** Turn dropped folders into the plan the office checks. */
  const plan = (folders: DroppedFolder[]) => {
    const planned: Group[] = folders.map((f) => {
      const person = f.name ? matchPerson(f.name, people) : null;
      const rows: Row[] = [...f.files]
        .sort((a, b) => sideOrder(a.name) - sideOrder(b.name) || a.name.localeCompare(b.name))
        .map((file) => {
          const accepted = isAcceptedUpload(file);
          const tooBig = file.size > (isImageUpload(file) ? MAX_IMAGE_BYTES : MAX_FILE_BYTES);
          const type = accepted && !tooBig ? matchType(file.name, workerTypes) : null;
          const photo = !tooBig && isPhoto(file, Boolean(type));
          return {
            key: nextKey(),
            file,
            typeCode: photo ? PHOTO : (type?.code ?? ''),
            expiresAt: type?.tracks_expiry ? (dateInName(file.name) ?? '') : '',
            state: 'ready',
            ...(!accepted && !photo
              ? { problem: t('bulk.notAccepted') }
              : tooBig
                ? { problem: t('bulk.tooBig', { size: fileSize(file.size) }) }
                : {}),
          };
        });
      return { key: nextKey(), folder: f.name, personId: person?.id ?? '', guessed: Boolean(person), rows };
    });
    setGroups((prev) => [...prev, ...planned.filter((g) => g.rows.length > 0)]);
  };

  const updateGroup = (key: string, change: Partial<Group>) =>
    setGroups((prev) => prev.map((g) => (g.key === key ? { ...g, ...change } : g)));
  const updateRow = (groupKey: string, rowKey: string, change: Partial<Row>) =>
    setGroups((prev) =>
      prev.map((g) => (g.key === groupKey ? { ...g, rows: g.rows.map((r) => (r.key === rowKey ? { ...r, ...change } : r)) } : g)),
    );

  /**
   * The rows of a group that need an expiry date: the first file of each dated item.
   * The others (an ID card's back) take the first one's dates.
   */
  const needsDate = (g: Group) => {
    const firstOf = new Set<string>();
    const out = new Set<string>();
    for (const r of g.rows) {
      const type = typeOf(r.typeCode);
      if (!type || r.state === 'done' || r.problem) continue;
      if (type.allows_multiple) {
        if (type.tracks_expiry) out.add(r.key);
      } else if (!firstOf.has(type.code)) {
        firstOf.add(type.code);
        if (type.tracks_expiry) out.add(r.key);
      }
    }
    return out;
  };

  const toUpload = groups.flatMap((g) => g.rows.filter((r) => r.typeCode && r.state !== 'done' && !r.problem));
  const missingPerson = groups.filter((g) => !g.personId && g.rows.some((r) => r.typeCode && r.state !== 'done'));
  const missingDates = groups.flatMap((g) => [...needsDate(g)].filter((k) => !g.rows.find((r) => r.key === k)?.expiresAt));
  const ready = toUpload.length > 0 && missingPerson.length === 0 && missingDates.length === 0;

  const upload = useMutation({
    mutationFn: async () => {
      let done = 0;
      setProgress({ done, total: toUpload.length });
      for (const g of groups) {
        // Per item: an ID card's front and back are one document, a folder's files are each their own.
        const heads = new Map<string, string>();
        for (const r of g.rows) {
          if (r.typeCode === PHOTO && r.state !== 'done') {
            try {
              await api.setUserPhoto(g.personId, r.file);
              updateRow(g.key, r.key, { state: 'done', error: undefined });
            } catch (error) {
              updateRow(g.key, r.key, {
                state: 'failed',
                error: error instanceof ApiError ? error.message : t('personal.photoFailed'),
              });
            }
            setProgress({ done: ++done, total: toUpload.length });
            continue;
          }
          const type = typeOf(r.typeCode);
          if (!type || r.state === 'done' || r.problem) continue;
          try {
            const head = type.allows_multiple ? undefined : heads.get(type.code);
            const { documentId } = await api.recordDocument(
              {
                typeCode: type.code,
                userId: g.personId,
                filename: r.file.name,
                ...(head ? { bundleWith: head } : r.expiresAt ? { expiresAt: r.expiresAt } : {}),
              },
              r.file,
            );
            if (!type.allows_multiple && !head) heads.set(type.code, documentId);
            updateRow(g.key, r.key, { state: 'done', error: undefined });
          } catch (error) {
            updateRow(g.key, r.key, {
              state: 'failed',
              error: error instanceof ApiError ? error.message : t('documents.uploadFailed'),
            });
          }
          setProgress({ done: ++done, total: toUpload.length });
        }
      }
    },
    onSettled: async () => {
      setProgress(null);
      for (const key of ['worker-register', 'compliance', 'alerts', 'documents', 'users', 'user', 'badge']) {
        await queryClient.invalidateQueries({ queryKey: [key] });
      }
    },
  });

  const close = () => {
    if (upload.isPending) return;
    setGroups([]);
    upload.reset();
    onClose();
  };

  const uploaded = groups.reduce((n, g) => n + g.rows.filter((r) => r.state === 'done').length, 0);
  const failed = groups.reduce((n, g) => n + g.rows.filter((r) => r.state === 'failed').length, 0);
  const finished = upload.isSuccess && failed === 0 && toUpload.length === 0;

  const typeOptions = workerTypes.map((x) => {
    const parent = x.parent_code ? workerTypes.find((p) => p.code === x.parent_code) : undefined;
    return { value: x.code, label: parent ? `${parent.label_it} › ${x.label_it}` : x.label_it };
  });

  return (
    <Modal
      open={open}
      onClose={close}
      size="xl"
      title={t('bulk.title')}
      description={groups.length === 0 ? t('bulk.hint') : undefined}
      footer={
        finished ? (
          <button type="button" className="btn-primary" onClick={close}>
            {t('common.close')}
          </button>
        ) : (
          <>
            <button type="button" className="btn-primary" disabled={!ready || upload.isPending} onClick={() => upload.mutate()}>
              {progress
                ? t('bulk.uploading', { done: progress.done, total: progress.total })
                : t('bulk.upload', { count: toUpload.length })}
            </button>
            <button type="button" className="btn-secondary" disabled={upload.isPending} onClick={close}>
              {t('common.cancel')}
            </button>
            <span className="text-xs text-muted ms-auto">
              {missingPerson.length > 0
                ? t('bulk.needPerson', { count: missingPerson.length })
                : missingDates.length > 0
                  ? t('bulk.needDates', { count: missingDates.length })
                  : null}
            </span>
          </>
        )
      }
    >
      <div className="space-y-4">
        {uploaded > 0 ? (
          <p className="rounded bg-success-soft px-3 py-2 text-sm font-semibold text-success">
            {t('bulk.done', { count: uploaded })}
            {failed > 0 ? ` · ${t('bulk.failedCount', { count: failed })}` : ''}
          </p>
        ) : null}

        {!upload.isPending && !finished ? (
          <div
            className={`rounded border-2 border-dashed px-4 py-6 text-center text-sm ${
              over ? 'border-[var(--color-primary)] bg-[var(--color-primary-soft)]' : 'border-border'
            }`}
            onDragOver={(e) => {
              e.preventDefault();
              setOver(true);
            }}
            onDragLeave={() => setOver(false)}
            onDrop={async (e) => {
              e.preventDefault();
              setOver(false);
              plan(await readDrop(e.dataTransfer.items));
            }}
          >
            <p className="font-semibold">{groups.length === 0 ? t('bulk.drop') : t('bulk.dropMore')}</p>
            <p className="text-xs text-muted mt-1">{t('bulk.dropExample')}</p>
            <button type="button" className="btn-secondary mt-3" onClick={() => picker.current?.click()}>
              {t('bulk.pick')}
            </button>
            <p className="text-xs text-muted mt-2">
              {t('bulk.pickNote')}{' '}
              <button type="button" className="text-primary hover:underline" onClick={() => filePicker.current?.click()}>
                {t('bulk.pickFiles')}
              </button>
            </p>
            <input
              ref={picker}
              type="file"
              className="hidden"
              multiple
              // A folder picker: the files arrive with their folder in webkitRelativePath.
              // Chrome asks "Upload N files to this site?" first, in its own box that no
              // page can style or skip; dragging the folder in does not ask.
              {...{ webkitdirectory: '', directory: '' }}
              onChange={(e) => {
                plan(groupByFolder([...(e.target.files ?? [])]));
                e.target.value = '';
              }}
            />
            <input
              ref={filePicker}
              type="file"
              className="hidden"
              multiple
              // Single files, no folder: they arrive together and the office chooses whose.
              onChange={(e) => {
                plan(groupByFolder([...(e.target.files ?? [])]));
                e.target.value = '';
              }}
            />
          </div>
        ) : null}

        {groups.map((g) => {
          const dated = needsDate(g);
          return (
            <section key={g.key} className="rounded border border-border">
              <header className="flex flex-wrap items-center gap-3 border-b border-border bg-surface-alt px-3 py-2">
                <span className="text-sm font-semibold">📁 {g.folder || t('bulk.looseFiles')}</span>
                <span className="text-xs text-muted">{forward}</span>
                <div className="min-w-[240px] flex-1 max-w-sm">
                  <Combobox
                    value={g.personId}
                    onChange={(id) => updateGroup(g.key, { personId: id, guessed: false })}
                    options={personOptions(people, L)}
                    placeholder={t('bulk.choosePerson')}
                    ariaLabel={t('bulk.person', { folder: g.folder })}
                    disabled={upload.isPending || g.rows.every((r) => r.state === 'done')}
                  />
                </div>
                <span className={`text-xs ${g.personId ? 'text-muted' : 'text-danger font-semibold'}`}>
                  {!g.personId ? t('bulk.noMatch') : g.guessed ? t('bulk.matched') : t('bulk.chosen')}
                </span>
                {!upload.isPending && g.rows.every((r) => r.state !== 'done') ? (
                  <button
                    type="button"
                    className="ms-auto text-xs text-muted hover:text-danger hover:underline"
                    onClick={() => setGroups((prev) => prev.filter((x) => x.key !== g.key))}
                  >
                    {t('bulk.removeFolder')}
                  </button>
                ) : null}
              </header>

              <ul className="divide-y divide-border">
                {g.rows.map((r) => {
                  const type = typeOf(r.typeCode);
                  const existing = type && !type.allows_multiple && g.personId ? onFile(g.personId, type.code) : undefined;
                  const locked = r.state === 'done' || upload.isPending;
                  return (
                    <li key={r.key} className="grid gap-2 px-3 py-2 text-sm sm:grid-cols-[minmax(0,1fr)_220px_150px] sm:items-center">
                      <div className="min-w-0">
                        <div className="truncate font-medium" title={r.file.name}>
                          {r.state === 'done' ? '✓ ' : ''}
                          {r.file.name}
                        </div>
                        <div className="text-xs text-muted">
                          {fileSize(r.file.size)}
                          {existing && r.state !== 'done'
                            ? ` · ${
                                existing.expiresAt
                                  ? t('bulk.replaces', { date: dateShort(existing.expiresAt) })
                                  : t('bulk.replacesUndated')
                              }`
                            : ''}
                        </div>
                        {r.problem || r.error ? <div className="text-xs text-danger">{r.problem ?? r.error}</div> : null}
                      </div>
                      <select
                        className="input h-9"
                        aria-label={t('bulk.typeFor', { name: r.file.name })}
                        value={r.typeCode}
                        disabled={locked || Boolean(r.problem)}
                        onChange={(e) => {
                          const next = typeOf(e.target.value);
                          updateRow(g.key, r.key, {
                            typeCode: e.target.value,
                            expiresAt: r.expiresAt || (next?.tracks_expiry ? (dateInName(r.file.name) ?? '') : ''),
                          });
                        }}
                      >
                        <option value="">{t('bulk.skip')}</option>
                        {/* The photo goes on the printed card: a picture, never a PDF. */}
                        <option value={PHOTO} disabled={!isImageFile(r.file)}>
                          {isImageFile(r.file) ? t('bulk.photo') : t('bulk.photoNeedsImage')}
                        </option>
                        {typeOptions.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                      {dated.has(r.key) ? (
                        <input
                          type="date"
                          className={`input h-9 ${r.expiresAt ? '' : 'border-[var(--color-danger)]'}`}
                          aria-label={t('bulk.expiryFor', { name: r.file.name })}
                          title={t('bulk.expiry')}
                          value={r.expiresAt}
                          disabled={locked}
                          onChange={(e) => updateRow(g.key, r.key, { expiresAt: e.target.value })}
                        />
                      ) : (
                        <span className="text-xs text-muted">
                          {r.state !== 'done' && type && !type.allows_multiple && type.tracks_expiry ? t('bulk.sameDates') : ''}
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>
          );
        })}
      </div>
    </Modal>
  );
}
